import { NotebookState } from '@mode/notebooks/contract';
import { isPresent } from '@mode/shared/util-js';
import { createSelector, Selector } from 'reselect';
import { NormalizedNotebooksArray, NormalizedNotebook } from '../notebook.types';
/*
Contains legacy store selector paths
TODO: Will contain NgRx selectors once we migrate legacy notebook state to NgRx state
Based on selectors in notebook.selectors.ts
*/

const notebookPath = (state: any) => state?.client?.notebooks;

const cellPath = (state: any) => notebookPath(state)?.cells;

function getNormalizedNotebook(notebooks: NormalizedNotebooksArray, token: string): NormalizedNotebook {
  return {
    token,
    pusherChannelName: notebooks.pusherChannelNames.get(token) ?? '',
    initialized: notebooks.initialized.get(token) ?? false,
    resource: notebooks.resources.get(token)!,
    session: notebooks.sessions.get(token)!,
    cellTokens: notebooks.cellTokensByNotebook.get(token)?.toArray() || [],
  };
}
export const getQueries = (state: any) => state?.client?.selectedReport?.queries;

export const notebookToken = (state: any) => notebookPath(state)?.books?.tokens?.first();

export const notebookTypesArray = (state: any) => notebookPath(state)?.books?.notebookTypes;

export const notebookSession = (state: any) => notebookPath(state)?.books?.sessions?.get(notebookToken(state));

export const notebookState = (state: any) => notebookPath(state)?.books?.resources;

export const selectedNotebookType = createSelector(notebookToken, notebookTypesArray, (notebookToken, types) =>
  types?.get(notebookToken)
);

export const notebookCellState = (state: any) => cellPath(state);

export const selectedCellToken = (state: any) => notebookPath(state)?.editor?.selectedCellToken;

export const cellContentTypesArray = (state: any) => cellPath(state)?.contentTypes;

export const cellContentResourcesArray = (state: any) => cellPath(state)?.resources;

export const notebookTokensArray = (state: any) => notebookPath(state).books.tokens;

export const notebookStatesArray = (state: any) => notebookPath(state).books.states;

export const selectedCellContentType = createSelector(
  selectedCellToken,
  cellContentTypesArray,
  (cellToken, cellContentsArray) => cellContentsArray?.get(cellToken)
);

export const selectedCellIsExported = createSelector(
  selectedCellToken,
  cellContentResourcesArray,
  (cellToken, cellResourcesArray) => {
    const cellResource = cellResourcesArray?.get(cellToken);
    return isPresent(cellResource?.local_dataset_exported_at);
  }
);

export const accountPreferenceSelector = (state: any) => state?.transient?.accountPreference;

export const accountContextPlanCode = (state: any) => state?.client?.accountContext?.plan_code;
export const notebookCellTokensArray = (state: any) => notebookPath(state).books.cellTokensByNotebook;
export const notebookAwaitingRunArray = (state: any) => notebookPath(state).books.awaitingInitialRun;

export const NotebookCellExecutingArray = (state: any) => cellPath(state).executing;
export const selectedNotebookToken = (state: any) => notebookTokensArray(state).first(null);
export const notebookResourcesArray = (state: any) => notebookPath(state).books.resources;
export const notebookSessionsArray = (state: any) => notebookPath(state).books.sessions;

export const isRunningByNotebook = createSelector(
  notebookCellTokensArray,
  NotebookCellExecutingArray,
  (state: any, notebookToken: string) => notebookToken,
  (tokensByNotebook, executing, notebookToken) => {
    const tokens = tokensByNotebook.get(notebookToken);
    if (!tokens) return false;

    return Array.from(tokens).some((token) => executing.get(token));
  }
);

export const stateByNotebook = createSelector(
  isRunningByNotebook,
  (state: any, notebookToken: string) => notebookStatesArray(state).get(notebookToken),
  (isRunning, notebookState) => (isRunning ? NotebookState.Running : notebookState)
) as any;

export const statusBarStateByNotebook = createSelector(
  (state: any, notebookToken: string) => stateByNotebook(state, notebookToken),
  (notebookState: NotebookState) => {
    switch (notebookState) {
      case NotebookState.Busy:
        return NotebookState.Error;
      case NotebookState.Session:
        return NotebookState.Loading;
      default:
        return notebookState;
    }
  }
);

export const selectedNotebookResource = createSelector(
  selectedNotebookToken,
  notebookResourcesArray,
  (notebookToken, resources) => resources.get(notebookToken)
);
export const normalizedNotebookByToken = createSelector(
  (state: any, notebookToken: string) => notebookToken,
  (state: any) => state.client.notebooks.books,
  (token, books) => getNormalizedNotebook(books, token)
);
export const sessionByNotebook = (state: any, notebookToken: string) => notebookSessionsArray(state).get(notebookToken);

export const stateMessageByNotebook = createSelector(
  [
    (state: any, notebookToken: string) => notebookToken,
    (state: any) => state,
    (state: any, notebookToken: string) => notebookToken,
  ],
  (notebookToken: string, state: any) => {
    const notebookStateValue = stateByNotebook(state, notebookToken);
    const session = sessionByNotebook(state, notebookToken);
    const resource = selectedNotebookResource(notebookToken);

    switch (notebookStateValue) {
      case NotebookState.Error:
        if (session) {
          switch (session.termination_reason) {
            case 'CRASHED':
              return 'Notebook has encountered an unexpected error';
            case 'EXPIRED':
              return 'Notebook session ended due to inactivity';
            case 'OOM':
              return 'Notebook ran out of memory';
            default:
              return 'Notebook session ended';
          }
        }
        return 'Notebook session ended';

      case NotebookState.Busy:
        return 'Notebook session currently unavailable';

      case NotebookState.Loading:
        return 'Loading data frames';

      case NotebookState.Session:
        return 'Setting up notebook';

      case NotebookState.Running:
        return resource && resource.warn ? '. Expect wait time due to increased Memory usage' : '';

      default:
        return '';
    }
  }
) as any;

export const canRunByNotebook = createSelector(
  sessionByNotebook,
  stateByNotebook,
  (session, state) => session && session.active && state === NotebookState.Ready
) as any;

export const selectedNotebookSession = createSelector(
  selectedNotebookToken,
  notebookSessionsArray,
  (notebookToken, sessions) => sessions.get(notebookToken)
) as any;
